/**
 * 参考视频上传组件（支持多个）
 *
 * - 仅支持本地上传（不支持手动输入 URL）。
 * - 选完文件后会先在浏览器端读取视频时长，
 *   若不在 [minDurationSec, maxDurationSec] 区间会直接报错并放弃上传；
 *   若加上后累计总时长会超过 totalMaxDurationSec 也会报错。
 * - 时长边界由调用方按 provider 能力声明传入；未传则不校验。
 * - 无论边界是否存在都会读取时长（输入侧计费需要），并把已上传项的时长
 *   随 onChange(value, durations) 一并交给调用方；服务端探测值优先。
 */

import { useRef, useState } from 'react';
import { Upload, X, Loader2, Film } from 'lucide-react';
import { platformFetch, readEnvelope } from '../../../platform/http';
import { readMediaDuration, formatSeconds } from '../utils/readMediaDuration';

interface VideoVideoReferenceUploaderProps {
  value: string[];
  /** 与 value 按下标对齐的时长列表（秒）；由父组件持有，组件内部不再维护 */
  durations: number[];
  /** value 与 durations 按下标对齐；移除项时双删 */
  onChange: (value: string[], durations: number[]) => void;
  maxCount?: number;
  /** 单条最小时长（秒） */
  minDurationSec?: number;
  /** 单条最大时长（秒） */
  maxDurationSec?: number;
  /** 多条累计总时长上限（秒） */
  totalMaxDurationSec?: number;
}

interface UploadVideoResponse {
  storageKey: string;
  /** 服务端 ffmpeg 探测的时长（秒）；探测失败为 null，回退浏览器读取值 */
  durationSec?: number | null;
}

const MAX_SIZE_MB = 50;
const ACCEPT = '.mp4,.mov,.webm,.mkv';

export function VideoVideoReferenceUploader({
  value,
  durations,
  onChange,
  maxCount = 1,
  minDurationSec,
  maxDurationSec,
  totalMaxDurationSec,
}: VideoVideoReferenceUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const canAdd = value.length < maxCount;

  const hasMin = typeof minDurationSec === 'number' && minDurationSec > 0;
  const hasMax = typeof maxDurationSec === 'number' && maxDurationSec > 0;
  const hasTotal = typeof totalMaxDurationSec === 'number' && totalMaxDurationSec > 0;

  const accumulated = durations.reduce((sum, d) => sum + d, 0);

  const durationHint = (() => {
    const parts: string[] = [];
    if (hasMin && hasMax) {
      parts.push(`单个时长 ${formatSeconds(minDurationSec!)}~${formatSeconds(maxDurationSec!)} 秒`);
    } else if (hasMin) {
      parts.push(`单个时长 ≥ ${formatSeconds(minDurationSec!)} 秒`);
    } else if (hasMax) {
      parts.push(`单个时长 ≤ ${formatSeconds(maxDurationSec!)} 秒`);
    }
    if (hasTotal) {
      parts.push(`总时长 ≤ ${formatSeconds(totalMaxDurationSec!)} 秒`);
    }
    return parts.join('，');
  })();

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`文件大小超过 ${MAX_SIZE_MB}MB 限制`);
      return;
    }
    if (!canAdd) return;

    setError('');

    // 1) 始终读取时长（计费需要）；若有时长边界则顺带校验
    let clientDuration = 0;
    try {
      clientDuration = await readMediaDuration(file, 'video');
    } catch (err: any) {
      // 无边界时读取失败可容忍（后端会兜底探测）；有边界时必须报错
      if (hasMin || hasMax || hasTotal) {
        setError(err?.message || '读取视频时长失败');
        return;
      }
    }
    let duration = clientDuration;
    if (hasMin && duration < (minDurationSec as number)) {
      setError(
        `视频时长 ${formatSeconds(duration)} 秒过短，需 ≥ ${formatSeconds(minDurationSec!)} 秒`
      );
      return;
    }
    if (hasMax && duration > (maxDurationSec as number)) {
      setError(
        `视频时长 ${formatSeconds(duration)} 秒过长，需 ≤ ${formatSeconds(maxDurationSec!)} 秒`
      );
      return;
    }
    if (hasTotal && accumulated + duration > (totalMaxDurationSec as number)) {
      setError(
        `加入后累计总时长 ${formatSeconds(accumulated + duration)} 秒，超过上限 ${formatSeconds(totalMaxDurationSec!)} 秒`
      );
      return;
    }

    // 2) 通过校验后上传
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // platformFetch 检测到 FormData 自动不设 Content-Type；401 统一清会话并广播
      const data = await readEnvelope<UploadVideoResponse>(
        await platformFetch('/videos/upload-video', {
          method: 'POST',
          body: formData,
        }),
        '上传失败'
      );

      // 服务端探测时长优先（计费权威口径），失败回退浏览器读取值
      const serverDuration = Number(data.durationSec);
      if (Number.isFinite(serverDuration) && serverDuration > 0) {
        duration = serverDuration;
      }

      // durations 与 value 按下标对齐（串行上传保证），移除时双删
      onChange([...value, data.storageKey], [...durations, duration]);
    } catch (err: any) {
      setError(err?.message || '上传失败');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx), durations.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-sm font-medium text-light-text">
          参考视频{maxCount > 1 ? `（最多 ${maxCount} 个）` : '（可选）'}
        </label>
      </div>

      {value.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {value.map((_item, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-light-border bg-light-hover"
            >
              <Film className="w-4 h-4 text-accent-blue flex-shrink-0" />
              <span className="text-xs text-light-text truncate flex-1">
                已上传 #{idx + 1}
                {durations[idx] ? `（${formatSeconds(durations[idx])}s）` : ''}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="p-0.5 rounded text-rose-500 hover:bg-rose-50"
                aria-label={`移除参考视频 #${idx + 1}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 上传按钮（只保留文件上传入口） */}
      {canAdd && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-light-border text-sm text-light-muted hover:border-accent-blue hover:text-accent-blue transition-all disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {uploading ? '上传中' : '上传文件'}
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />

      {error && <p className="mt-1 text-xs text-rose-500">{error}</p>}
      <p className="mt-1 text-xs text-light-muted">
        支持 mp4/mov/webm/mkv，≤{MAX_SIZE_MB}MB
        {durationHint ? `，${durationHint}` : ''}
      </p>
    </div>
  );
}
